import * as _ from 'lodash-es';
import { configureAuth } from 'react-query-auth';

import {
  TK_USER_ME,
  API_CTX,
  isProdEnv,
  NO_COMPANY_ROLE_ASSIGNED,
  ADMIN_PANEL_OWNER,
} from '@/config';
import { resetBaseURL, errorResponseToInfo } from '@/lib/axios-v1';
import {
  AuthUser,
  CompanyRole,
  UserResponse,
  RegisterValues,
  RegisterCredentialsDTO,
  LoginCredentialsDTO,
  ForgotPasswordDTO,
  PasswordResetResponse,
  ROLES,
} from '@/types';
import { error, log, warn, uploadException } from '@/utils/logger';
import storage from '@/utils/storage';

import { isHardCodedAdmin } from './user';
import { LoginInput } from './zod';

/**
 * for explicit logout mark, used in protected route
 * @2025/07/04
 */
export const exitHash = 'exit';

/**
 * FIXME: this function is re-used by:
 * - ContributorMenu
 * - useForgotPasswordAPI
 * so, put it here!
 * hopefully move it to auth feature
 * @param data
 * @returns
 */
export const requestPasswordReset = (
  data: ForgotPasswordDTO,
): Promise<PasswordResetResponse> => {
  return resetBaseURL().post(`${API_CTX}/password-reset/request`, data);
};

/**
 * === getUser with error handling =====
 * @date 2024/08/14
 * @returns
 */
const getTKUser = async (): Promise<AuthUser> => {
  try {
    return resetBaseURL().get(TK_USER_ME);
  } catch (error) {
    const { message } = errorResponseToInfo(error);
    const err = new Error(message);
    // FIXME: do not upload this error, unnecessary!
    // @2024/08/24
    uploadException(err, true);
    throw err;
  }
};

const loginTK = (data: LoginInput): Promise<UserResponse> => {
  return resetBaseURL().post(`${API_CTX}/login`, data);
};

// === TK register function ===
export const registerTKAccount = (
  data: RegisterCredentialsDTO,
): Promise<UserResponse> => {
  return resetBaseURL().post(`${API_CTX}/users`, data);
};

export function handleUserResponse(data: UserResponse) {
  const { token, ...user } = data;
  if (token) {
    // update token in browser cache
    storage.setToken(token);
    storage.setLoginTime();
  }
  return user;
}

/**
 * Fetch user info with cached token, result could be `null` if token expired.
 * Stronger way to load user through token, with `try...catch`.
 *
 * @returns user | null
 */
async function userCheckWithToken(): Promise<AuthUser | null> {
  const isTestingMode = !!window['TESTING_MODE'];
  if (isTestingMode) {
    log(`>>> Running get TKUser() in testing ...`);
    return getTKUser();
  }

  // check token expiry first
  const isExpired = storage.isTokenExpired();
  if (isExpired) {
    const lastLogin = storage.getLoginTime();
    log(`### using expired token, last login time is: ${lastLogin}`);
    // if expired detected, return null
    return null;
  }
  if (storage.getToken()) {
    // FIXME: trying to get logged in user info with a cached token ...
    // null returned, if token is expired
    // @2023/09/07
    try {
      const response = await getTKUser();
      return handleUserResponse(response);
    } catch (err) {
      error(`## GOT error on fetching user!!!`);
      return null;
    }
  }
  log('## No token no user! goto login...');
  return null;
}

async function loginFn(data: LoginCredentialsDTO) {
  try {
    const response = await loginTK(data);
    return handleUserResponse(response);
  } catch (error: any) {
    // no need to display cos axis interceptor handled the error!
    console.error(error);
    return null;
  }
}

async function registerFn(data: RegisterValues): Promise<AuthUser | null> {
  const { email, name, password } = data;
  const response = await registerTKAccount({ email, name, password });
  const user = handleUserResponse(response);
  return user;
}
/**
 * close backend session
 * @date 2024/11/22
 * @returns void
 */
const logoutFromSystem = (): Promise<void> => {
  return resetBaseURL().post(`${API_CTX}/logout`);
};

/**
 * use hash-tag to tell proected do proper redirection!
 */
async function logoutFn() {
  // ! FIRST: add a hash tag to mark exit!
  // @ 2025/07/04
  window.location.hash = exitHash;
  // then, logout with token
  await logoutFromSystem();
  // last, clear useless token
  storage.clearToken();
}

// === for TaksoAI ===
const authConfigTK = {
  userFn: userCheckWithToken,
  loginFn,
  registerFn,
  logoutFn,
};

export const { useUser, useLogin, useLogout, useRegister, AuthLoader } =
  configureAuth(authConfigTK);

/**
 * Wrapper for useUser
 * @returns
 */
export const useAuth = () => {
  const userResp = useUser();
  return userResp.data;
};

/**
 * cache latest using company, may change by ADMIN of taksoai
 * @date 2025/10/29
 */
let latestCompanyID = 0;
let latestCompanyName = '';

export const switchCompanyByAdmin = (
  companyID: number,
  companyName: string,
) => {
  latestCompanyID = companyID;
  latestCompanyName = companyName;
};

export type CompanyOBJ = {
  company: any;
};

export const getLatestCompany = (
  user: AuthUser | undefined | null,
): CompanyOBJ => {
  // check latest set first:
  if (latestCompanyID) {
    return {
      company: {
        id: latestCompanyID,
        name: latestCompanyName,
      },
    };
  }
  if (!user) return { company: undefined };
  if (user.company_roles.length == 0) {
    return { company: undefined };
  }
  return user.company_roles.at(0) as CompanyOBJ;
};

export const getCompanyIdFrom = (
  user: AuthUser | undefined | null,
): number | undefined => {
  if (!user) return undefined;

  // == use next company if it changed by admin
  // @2025/10/29
  if (latestCompanyID) return latestCompanyID;

  // FIXME: check company role for new user
  // @2024/03/05
  const orgRoles = _.get(user, 'company_roles', []);
  if (!orgRoles.length) {
    // toaπst.warn(NO_COMPANY_ROLE_ASSIGNED);
    warn('>> No company role attached!');
    return undefined;
  }
  // company_roles could be [] if user not assigned
  // ! FIXME: get the latest associated company to open!
  // @2025/10/01, 10/03
  const latestCompanyId = user.company_roles.at(0)?.company.id;
  if (!latestCompanyId) {
    warn(NO_COMPANY_ROLE_ASSIGNED);
    return undefined;
  }
  return latestCompanyId;
};

export const checkCompanyAvailable = (
  user: AuthUser | undefined | null,
): boolean => {
  if (!user) return false;

  const orgRolse = _.get(user, 'company_roles', []);
  return !!orgRolse.length || !isProdEnv;
};

/**
 * Check current user if TaksoAI admin
 * @param user
 * @returns
 */
export const isTasoAIAdmin = (user: AuthUser | undefined | null) => {
  if (!user) return false;
  const email = user.email;
  // inline admins
  const isAdmin = isHardCodedAdmin(email);
  if (isAdmin) {
    log(`## welcome admin: ${user.name} have a nice play day !##`);
    return true;
  }

  // additional admins
  const condition = (cr: CompanyRole) =>
    cr.company.id == Number(ADMIN_PANEL_OWNER);
  const owner = user.company_roles.find(condition);
  if (!owner) return false;
  const roleType = owner.roles[0].name;
  return roleType === ROLES.ADMIN;
};
